import React, { useState, useCallback } from 'react';
import { TabState, createDatasetId, createTransformOperationId, DataValue, DatasetId } from '../../../../types';
import { DerivedDataSet, ExcludedRow } from '../../../../types/dataset.types';
import { ROW_ID_COLUMN_NAME } from '../../../../types/system-columns';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { useTabOperations } from '../../../hooks/useTabOperations';
import { useProjectStore } from '../../../stores/projectStore';
import { useNotificationStore } from '../../../stores/notificationStore';
import { ColumnConversion, generateConversionSQL, generateExcludeRowsSQL } from '../../../utils/column-type-conversion';
import { executeOperation } from '../../../utils/executeOperation';
import { createDataTableTab } from '../../../tab-system/TabRegistry';
import { Step1Settings, Step2Preview } from '../../ColumnTypeConversionSteps';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import logger from '../../../utils/logger';
import * as styles from './ColumnTypeConversionTabContent.css.ts';

/**
 * ColumnTypeConversionTabContent
 *
 * 列の型変換を設定・プレビューするタブ
 * 2カラムレイアウト：左側に設定、右側にプレビュー
 * ダイアログの実装を再利用
 */

interface ColumnTypeConversionTabContentProps {
  tab: TabState;
}

export const ColumnTypeConversionTabContent: React.FC<ColumnTypeConversionTabContentProps> = ({ tab }) => {
  const { project, datasets, addDerivedDataset } = useProjectData();
  const paneLayout = usePaneLayout();
  const updateTabTitle = useProjectStore((state) => state.updateTabTitle);
  const updateTabState = useProjectStore((state) => state.updateTabState);
  const findTab = useProjectStore((state) => state.findTab);
  const { error: showError } = useNotificationStore();
  const tabOperations = useTabOperations({
    project,
    layout: {
      rootPane: paneLayout.layout.rootPane,
      activeContainerId: paneLayout.layout.activeContainerId ?? null
    },
    addTab: paneLayout.addTab,
    addTabWithOptions: paneLayout.addTabWithOptions,
    closeTab: paneLayout.closeTab,
    moveTabWithinPane: paneLayout.moveTabWithinPane,
    moveTabToPane: paneLayout.moveTabToPane,
    startDrag: paneLayout.startDrag,
    endDrag: paneLayout.endDrag
  });

  // データセット選択状態（ローカル状態で管理）
  const [selectedDatasetId, setSelectedDatasetId] = useState<DatasetId | undefined>(
    tab.metadata?.datasetId
  );
  const selectedColumnId = tab.metadata?.columnId;

  const dataset = selectedDatasetId ? datasets[selectedDatasetId] : undefined;

  // 変換設定の状態
  const [step, setStep] = useState<1 | 2>(1);  // 1: 設定画面, 2: 全量プレビュー画面
  const [conversions, setConversions] = useState<ColumnConversion[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  // データセット名入力ダイアログ
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');

  // Resizer状態
  const [leftWidth, setLeftWidth] = useState(45); // パーセント
  const [isResizing, setIsResizing] = useState(false);

  // データセット選択ハンドラー
  const handleDatasetChange = useCallback((newDatasetId: DatasetId) => {
    setSelectedDatasetId(newDatasetId);

    // 変換設定をリセット
    setConversions([]);
    setStep(1);

    // タブタイトルを更新
    const newDataset = datasets[newDatasetId];
    if (newDataset) {
      updateTabTitle(tab.id, `Convert Column Types: ${newDataset.name}`);
      logger.info('[ColumnTypeConversionTabContent] Dataset changed to:', newDataset.name);
    }

    // TabStateのmetadataを更新して永続化
    const result = findTab(tab.id);
    if (result) {
      updateTabState(result.container.id, tab.id, (currentTab) => ({
        ...currentTab,
        metadata: {
          ...currentTab.metadata,
          datasetId: newDatasetId
        }
      }));
      logger.info('[ColumnTypeConversionTabContent] Updated tab metadata with datasetId:', newDatasetId);
    }
  }, [datasets, tab.id, updateTabTitle, findTab, updateTabState]);

  // 初回マウント時: datasetIdがあればタイトル更新
  React.useEffect(() => {
    if (selectedDatasetId && dataset) {
      updateTabTitle(tab.id, `Convert Column Types: ${dataset.name}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回マウント時のみ実行

  // Cancelハンドラー
  const handleCancel = useCallback(() => {
    const containerId = tabOperations.getActiveContainerId();
    if (containerId) {
      paneLayout.closeTab(containerId, tab.id);
    }
  }, [tab.id, tabOperations, paneLayout]);

  // Backハンドラー
  const handleBack = useCallback(() => {
    setStep(1);
  }, []);

  // Previewハンドラー
  const handlePreview = useCallback(() => {
    setStep(2);
  }, []);

  // Resizerハンドラー
  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const container = document.querySelector('[data-resizable-container]');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // 20%から80%の範囲で制限
    if (newLeftWidth >= 20 && newLeftWidth <= 80) {
      setLeftWidth(newLeftWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // マウスイベントリスナーを登録
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Apply準備ハンドラー（ダイアログを表示）
  const handlePrepareApply = useCallback(() => {
    if (!dataset) return;

    // デフォルト名を設定してダイアログを表示
    setNewDatasetName(`${dataset.name} (Type Converted)`);
    setShowNameDialog(true);
  }, [dataset]);

  // 実際のApply処理
  const handleConfirmApply = useCallback(async () => {
    if (!dataset || !selectedDatasetId || !project || !newDatasetName.trim()) {
      logger.warn('[ColumnTypeConversionTabContent] Cannot apply: missing required data');
      return;
    }

    setShowNameDialog(false);
    setIsApplying(true);

    try {
      // SQL生成
      const { sql: conversionSQL, needsExcludeQuery, excludeQueryColumns } = generateConversionSQL(
        dataset.name,  // テーブル名を渡す（selectedDatasetIdではなく）
        dataset.schema.columns,
        conversions
      );

      logger.debug('[ColumnTypeConversionTabContent] Generated conversion SQL', { sql: conversionSQL });

      // SQLクエリを実行
      const result = await executeOperation(
        {
          id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
          type: 'sql_query',
          query: conversionSQL,
          targetDatasetId: selectedDatasetId
        },
        { [selectedDatasetId]: dataset },
        selectedDatasetId
      );

      // Exclude modeの列がある場合、除外行を処理
      let excludedRowsMap: Record<number, ExcludedRow> | undefined;
      logger.info('[ColumnTypeConversionTabContent] Checking EXCLUDE mode', {
        needsExcludeQuery,
        excludeQueryColumnsCount: excludeQueryColumns.length,
        excludeQueryColumns
      });
      if (needsExcludeQuery && excludeQueryColumns.length > 0) {
        const excludeSQL = generateExcludeRowsSQL(
          dataset.name,
          excludeQueryColumns.map(col => ({
            name: col,
            fromType: conversions.find(c => c.columnName === col)!.fromType,
            toType: conversions.find(c => c.columnName === col)!.toType
          }))
        );

        logger.debug('[ColumnTypeConversionTabContent] Generated exclude SQL', { sql: excludeSQL });

        // 除外行を取得
        const excludeResult = await executeOperation(
          {
            id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
            type: 'sql_query',
            query: excludeSQL,
            targetDatasetId: selectedDatasetId
          },
          { [selectedDatasetId]: dataset },
          selectedDatasetId
        );

        // 除外行IDsを取得
        const excludeRowIds: number[] = [];

        // excludeResultから"source_row_id"列を探す
        // 注意: generateExcludeRowsSQLはRow #列を別名（source_row_id）で取得する
        // これは、arrowToOperationResultがRow #列を自動的に削除・再生成するのを回避するため
        const rowIdColumnDef = excludeResult.schema?.columns.find(col => col.name === 'source_row_id');
        logger.debug('[ColumnTypeConversionTabContent] Exclude query result', {
          hasRowIdColumnDef: !!rowIdColumnDef,
          rowCount: excludeResult.rowCount,
          availableColumns: excludeResult.schema?.columns.map(c => c.name),
          resultKeys: Object.keys(excludeResult.data || {})
        });

        if (rowIdColumnDef && excludeResult.data) {
          const rowIdColumn = excludeResult.data[rowIdColumnDef.id];
          if (rowIdColumn) {
            for (let i = 0; i < (excludeResult.rowCount ?? 0); i++) {
              const rowId = rowIdColumn[i];
              if (typeof rowId === 'number') {
                excludeRowIds.push(rowId);
              }
            }
          }
        }

        logger.debug('[ColumnTypeConversionTabContent] Collected exclude row IDs', {
          excludeRowIds
        });

        // Derived Datasetの除外行を作成
        if (excludeRowIds.length > 0) {
          excludedRowsMap = {};
          const excludeRowIdsSet = new Set(excludeRowIds);

          // 除外する行のデータを収集してRecordに追加
          excludeRowIds.forEach(rowId => {
            const rowData: Record<string, DataValue> = {};
            result.schema.columns.forEach(col => {
              const colData = result.data?.[col.id];
              if (colData && rowId < colData.length) {
                rowData[col.id] = colData[rowId];
              }
            });

            excludedRowsMap![rowId] = {
              data: rowData,
              comment: 'Type conversion failed (exclude mode)',
              excludedAt: new Date().toISOString()
            };
          });

          // 除外行をresult.dataから削除
          if (result.data) {
            // Row #列を名前で検索
            const resultRowIdColumnDef = result.schema.columns.find(col => col.name === ROW_ID_COLUMN_NAME);
            if (!resultRowIdColumnDef) {
              logger.error('[ColumnTypeConversionTabContent] Row ID column not found in result schema', {
                availableColumns: result.schema.columns.map(c => c.name)
              });
            } else {
              const rowIdData = result.data[resultRowIdColumnDef.id];
              if (!rowIdData) {
                logger.error('[ColumnTypeConversionTabContent] Row ID column data not found', {
                  columnId: resultRowIdColumnDef.id,
                  dataKeys: Object.keys(result.data)
                });
              } else {
                const filteredData: Record<string, DataValue[]> = {};
                result.schema.columns.forEach(col => {
                  const colData = result.data?.[col.id];
                  if (colData) {
                    // Row #値を確認して、除外対象でない行のみを保持
                    filteredData[col.id] = colData.filter((_, index) => {
                      const rowIdValue = rowIdData[index];
                      return !excludeRowIdsSet.has(rowIdValue as number);
                    });
                  }
                });
                result.data = filteredData;
                result.rowCount = (result.rowCount ?? 0) - excludeRowIds.length;
              }
            }
          }

          logger.info('[ColumnTypeConversionTabContent] Excluded rows from derived dataset', {
            count: excludeRowIds.length,
            remainingRows: result.rowCount,
            excludeRowIds: Array.from(excludeRowIds),
            originalRowCount: result.rowCount + excludeRowIds.length
          });
        }
      }

      // Derived Dataset作成
      const timestamp = Date.now();
      const derivedDataset: DerivedDataSet = {
        id: createDatasetId(`converted_${timestamp}`),
        name: newDatasetName.trim(),
        type: 'derived' as const,
        schema: result.schema,
        data: result.data,
        rowCount: result.rowCount,
        parentDatasetIds: [selectedDatasetId],
        operation: {
          id: createTransformOperationId(`type_conversion_${timestamp}`),
          type: 'sql_query' as const,
          query: conversionSQL,
          targetDatasetId: selectedDatasetId
        },
        exclusions: excludedRowsMap ? {
          excludedRows: excludedRowsMap
        } : undefined,
        metadata: {
          createdAt: new Date().toISOString(),
          basedOn: [selectedDatasetId]
        }
      };

      // Derived Datasetを追加
      addDerivedDataset(derivedDataset);

      // 新しいタブで結果を開く
      const activeContainerId = tabOperations.getActiveContainerId();
      if (activeContainerId) {
        const newTab = createDataTableTab(derivedDataset.id, derivedDataset.name);
        paneLayout.addTab(activeContainerId, newTab);
      }

      // 現在のタブを閉じる
      handleCancel();

      logger.info('[ColumnTypeConversionTabContent] Successfully created converted dataset:', derivedDataset.id);
    } catch (error) {
      logger.error('[ColumnTypeConversionTabContent] Failed to apply conversion:', error);
      showError(`Failed to apply conversion: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsApplying(false);
    }
  }, [dataset, selectedDatasetId, project, newDatasetName, conversions, addDerivedDataset, tabOperations, paneLayout, handleCancel, showError]);

  // データセット選択ドロップダウン（全画面で表示）
  const datasetSelector = (
    <div className={styles.datasetSelector}>
      <label htmlFor="dataset-select" className={styles.datasetSelectorLabel}>Dataset:</label>
      <select
        id="dataset-select"
        className={styles.datasetSelectorSelect}
        value={selectedDatasetId ?? ''}
        onChange={(e) => handleDatasetChange(e.target.value as DatasetId)}
        disabled={Object.keys(datasets).length === 0}
      >
        <option value="" disabled>
          {Object.keys(datasets).length === 0
            ? 'No datasets available'
            : 'Select a dataset...'}
        </option>
        {Object.values(datasets).map(ds => (
          <option key={ds.id} value={ds.id}>
            {ds.name} ({ds.type})
          </option>
        ))}
      </select>
    </div>
  );

  // データセット未選択時の表示
  if (!dataset) {
    return (
      <div className={styles.fullContainer}>
        {datasetSelector}
        <div className={styles.container}>
          {/* 左カラム: 設定エリアのプレースホルダー */}
          <div className={styles.leftColumn}>
            <div className={styles.placeholder}>
              <p>Select a dataset to configure column type conversions.</p>
            </div>
          </div>

          {/* Resizer */}
          <div className={styles.resizer} />

          {/* 右カラム: プレビューエリアのプレースホルダー */}
          <div className={styles.rightColumn}>
            <div className={styles.placeholder}>
              <p>Preview will appear here after dataset selection.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step1: 設定画面（2カラムレイアウト）
  if (step === 1) {
    return (
      <div className={styles.fullContainer}>
        {datasetSelector}
        <div className={styles.container} data-resizable-container>
        {/* 左カラム: 設定画面 */}
        <div className={styles.leftColumn} style={{ flex: `0 0 ${leftWidth}%` }}>
          <div className={styles.settingsWrapper}>
            <Step1Settings
              dataset={dataset}
              initialColumnId={selectedColumnId}
              conversions={conversions}
              onConversionsChange={setConversions}
            />
          </div>

          {/* ボタン */}
          <div className={styles.buttonContainer}>
            <div className={styles.spacer} />
            <button className={styles.secondaryButton} onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={styles.primaryButton}
              onClick={handlePreview}
              disabled={conversions.filter(c => c.enabled && c.fromType !== c.toType).length === 0}
            >
              Preview
            </button>
          </div>
        </div>

        {/* Resizer */}
        <div className={styles.resizer} onMouseDown={handleMouseDown} />

        {/* 右カラム: サンプルプレビュー */}
        <div className={styles.rightColumn}>
          <Step2Preview
            dataset={dataset}
            conversions={conversions}
            mode="sample"
          />
        </div>
        </div>
      </div>
    );
  }

  // Step2: 全量プレビュー画面（全画面）
  return (
    <div className={styles.fullContainer}>
      {datasetSelector}
      <div className={styles.fullScreenContainer}>
      {/* 全量プレビュー */}
      <div className={styles.previewColumn}>
        <Step2Preview
          dataset={dataset}
          conversions={conversions}
          mode="full"
        />
      </div>

      {/* ボタン */}
      <div className={styles.buttonContainer}>
        <div className={styles.spacer} />
        <button className={styles.secondaryButton} onClick={handleBack}>
          Back
        </button>
        <button
          className={styles.primaryButton}
          onClick={handlePrepareApply}
          disabled={isApplying}
        >
          {isApplying ? 'Applying...' : 'Apply'}
        </button>
      </div>

      {/* データセット名入力ダイアログ */}
      {showNameDialog && (
        <Dialog
          isOpen={showNameDialog}
          onClose={() => setShowNameDialog(false)}
          title="Save Converted Dataset"
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowNameDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmApply}
                disabled={!newDatasetName.trim()}
              >
                OK
              </Button>
            </>
          }
        >
          <div style={{ padding: '16px' }}>
            <label htmlFor="dataset-name" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Dataset Name:
            </label>
            <input
              id="dataset-name"
              type="text"
              value={newDatasetName}
              onChange={(e) => setNewDatasetName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #d0d0d0',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDatasetName.trim()) {
                  handleConfirmApply();
                }
              }}
            />
          </div>
        </Dialog>
      )}
      </div>
    </div>
  );
};
